/**
 * psref.ts
 *
 * PineSeed reference (psref:///) URI building and parsing.
 * Canonical slug ↔ kind mappings and text scanning.
 *
 * Uses numeric kind values — no proto enum imports (erasableSyntaxOnly safe).
 * Source of truth: agency/reference/v1/reference.proto.
 */

import type { Reference } from "./mdc";

// ─── Kind constants ──────────────────────────────────────────────────────────

const KIND_PARTICIPANT = 1;
const KIND_GROUP = 2;
const KIND_GENERAL = 3;
const KIND_URI = 4;
const KIND_FILE = 5;
const KIND_MEDIA = 6;
const KIND_CHAT_MESSAGE = 7;
const KIND_WORKSPACE = 8;
const KIND_THREAD = 9;
const KIND_CONVERSATION = 10;

export {
  KIND_PARTICIPANT,
  KIND_GROUP,
  KIND_GENERAL,
  KIND_URI,
  KIND_FILE,
  KIND_MEDIA,
  KIND_CHAT_MESSAGE,
  KIND_WORKSPACE,
  KIND_THREAD,
  KIND_CONVERSATION,
};

// ─── Slug mappings ───────────────────────────────────────────────────────────

const KIND_TO_SLUG: Record<number, string> = {
  [KIND_PARTICIPANT]: "participant",
  [KIND_GROUP]: "group",
  [KIND_GENERAL]: "general",
  [KIND_URI]: "uri",
  [KIND_FILE]: "file",
  [KIND_MEDIA]: "media",
  [KIND_CHAT_MESSAGE]: "chat-message",
  [KIND_WORKSPACE]: "workspace",
  [KIND_THREAD]: "thread",
  [KIND_CONVERSATION]: "conversation",
};

const SLUG_TO_KIND: Record<string, number> = {
  participant: KIND_PARTICIPANT,
  group: KIND_GROUP,
  general: KIND_GENERAL,
  uri: KIND_URI,
  file: KIND_FILE,
  media: KIND_MEDIA,
  "chat-message": KIND_CHAT_MESSAGE,
  workspace: KIND_WORKSPACE,
  thread: KIND_THREAD,
  conversation: KIND_CONVERSATION,
};

// ─── MimeResolver ────────────────────────────────────────────────────────────

/**
 * Maps a file extension (no dot) to a MIME type string.
 * Injected at the app boundary — never provided by this lib.
 */
export type MimeResolver = (ext: string) => string;

// ─── buildPsrefUri ───────────────────────────────────────────────────────────

/**
 * Build a canonical psref:/// URI from structured Reference fields.
 * Throws on unknown kind or missing id — fail-fast.
 */
export function buildPsrefUri(
  ref: Pick<
    Reference,
    "kind" | "id" | "ext" | "version" | "tenant" | "params"
  >,
): string {
  const slug = KIND_TO_SLUG[ref.kind];
  if (!slug) {
    throw new Error(`buildPsrefUri: cannot build URI for kind ${ref.kind}`);
  }
  if (!ref.id) {
    throw new Error("buildPsrefUri: id is required");
  }

  const url = new URL(`psref:///${slug}`);
  url.searchParams.set("id", ref.id);
  if (ref.ext) url.searchParams.set("ext", ref.ext);
  if (ref.version) url.searchParams.set("version", ref.version);
  if (ref.tenant) url.searchParams.set("tenant", ref.tenant);
  for (const [k, v] of Object.entries(ref.params ?? {})) {
    url.searchParams.set(k, v);
  }

  return url.toString();
}

// ─── REFERENCE_PATTERN ───────────────────────────────────────────────────────

// Matches: [@title](uri "description") or ![@title](uri "description")
// Factory — callers must not share instances across concurrent uses.
export const REFERENCE_PATTERN = (): RegExp =>
  /(!?)\[@([^\]]*)\]\(([^)"]+?)(?:\s+"([^"]*)")?\)/g;

// ─── ParsedReference ─────────────────────────────────────────────────────────

export interface ParsedReference {
  reference: Reference;
  start: number;
  end: number;
  raw: string;
  isImage: boolean;
  kindSlug: string;
}

// ─── parsePsrefUri (internal) ────────────────────────────────────────────────

const STRUCTURAL_KEYS = new Set(["id", "version", "tenant", "ext"]);

function parsePsrefUri(
  uri: string,
  mimeResolver: MimeResolver | undefined,
): Pick<
  Reference,
  "kind" | "id" | "ext" | "type" | "version" | "tenant" | "params"
> & { kindSlug: string } {
  const url = new URL(uri);
  if (url.protocol !== "psref:") {
    throw new Error(
      `reference: unsupported scheme "${url.protocol}", expected "psref:"`,
    );
  }
  if (url.hostname !== "") {
    throw new Error(
      `reference: psref URI must have empty host, got "${url.hostname}"`,
    );
  }

  const slug = url.pathname.replace(/^\//, "");
  if (!slug) {
    throw new Error("reference: psref URI missing kind path segment");
  }
  if (slug.includes("/")) {
    throw new Error(
      `reference: psref URI path must have exactly one segment, got "${url.pathname}"`,
    );
  }

  const kind = SLUG_TO_KIND[slug];
  if (kind === undefined) {
    throw new Error(`reference: unknown kind slug "${slug}"`);
  }

  const id = url.searchParams.get("id");
  if (!id) {
    throw new Error(
      'reference: psref URI missing required query param "id"',
    );
  }

  const ext = url.searchParams.get("ext") ?? "";
  if (ext && !mimeResolver) {
    throw new Error(
      `reference: mimeResolver is required to resolve ext "${ext}"`,
    );
  }
  const type = ext ? mimeResolver!(ext) : "";

  const params: Record<string, string> = {};
  for (const [k, v] of url.searchParams.entries()) {
    if (!STRUCTURAL_KEYS.has(k)) {
      params[k] = v;
    }
  }

  return {
    kind,
    kindSlug: slug,
    id,
    ext,
    type,
    version: url.searchParams.get("version") ?? "",
    tenant: url.searchParams.get("tenant") ?? "",
    params: Object.keys(params).length > 0 ? params : {},
  };
}

// ─── parseReferences ─────────────────────────────────────────────────────────

/**
 * Scan text for all PineSeed reference syntax occurrences.
 * Returns structured ParsedReference objects in order of appearance.
 * Throws on any malformed psref reference — fail-fast.
 */
export function parseReferences(
  text: string,
  mimeResolver?: MimeResolver,
): ParsedReference[] {
  if (!text.includes("psref:")) return [];
  const results: ParsedReference[] = [];
  let match: RegExpExecArray | null;

  const pattern = REFERENCE_PATTERN();

  while ((match = pattern.exec(text)) !== null) {
    const raw = match[0];
    const imageFlag = match[1] ?? "";
    const title = match[2] ?? "";
    const uri = match[3] ?? "";
    const description = match[4] ?? "";

    const isImage = imageFlag === "!";
    if (!uri.startsWith("psref:")) continue;

    const { kindSlug, ...structured } = parsePsrefUri(uri, mimeResolver);

    const reference: Reference = {
      ...structured,
      title,
      description,
      uri,
      psrefUri: uri,
    };

    results.push({
      reference,
      kindSlug,
      start: match.index,
      end: match.index + raw.length,
      raw,
      isImage,
    });
  }

  return results;
}

// ─── kindFromMime ────────────────────────────────────────────────────────────

/**
 * Determine reference kind from MIME type.
 * Image/audio/video → KIND_MEDIA, everything else → KIND_FILE.
 */
export function kindFromMime(mimeType: string): number {
  return mimeType.startsWith("image/") ||
    mimeType.startsWith("audio/") ||
    mimeType.startsWith("video/")
    ? KIND_MEDIA
    : KIND_FILE;
}
